import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { Search, SlidersHorizontal, X } from "lucide-react";

const GENDERS = ["male", "female", "co-ed"];

export default function Properties() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [city, setCity] = useState(params.get("city") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const [minPrice, setMinPrice] = useState(params.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max_price") || "");
  const [gender, setGender] = useState(params.get("gender") || "");
  const [college, setCollege] = useState(params.get("college") || "");

  const fetchData = async () => {
    setLoading(true);
    const p = {};
    if (city) p.city = city;
    if (q) p.q = q;
    if (minPrice) p.min_price = minPrice;
    if (maxPrice) p.max_price = maxPrice;
    if (gender) p.gender = gender;
    if (college) p.college = college;
    try {
      const { data } = await api.get("/properties", { params: p });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [params]);

  const applyFilters = (e) => {
    e?.preventDefault();
    const sp = {};
    if (city) sp.city = city;
    if (q) sp.q = q;
    if (minPrice) sp.min_price = minPrice;
    if (maxPrice) sp.max_price = maxPrice;
    if (gender) sp.gender = gender;
    if (college) sp.college = college;
    setParams(sp);
    setFiltersOpen(false);
  };

  const clearAll = () => {
    setCity(""); setQ(""); setMinPrice(""); setMaxPrice(""); setGender(""); setCollege("");
    setParams({});
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition text-sm";
  const labelCls = "text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block";

  const FiltersBody = (
    <form onSubmit={applyFilters} className="space-y-5" data-testid="filters-form">
      <div>
        <label className={labelCls}>Search</label>
        <input className={inputCls} value={q} onChange={e=>setQ(e.target.value)} placeholder="title, area..." data-testid="filter-q"/>
      </div>
      <div>
        <label className={labelCls}>City</label>
        <input className={inputCls} value={city} onChange={e=>setCity(e.target.value)} placeholder="Jaipur, Bengaluru..." data-testid="filter-city"/>
      </div>
      <div>
        <label className={labelCls}>College</label>
        <input className={inputCls} value={college} onChange={e=>setCollege(e.target.value)} placeholder="IIT, Poornima, DU..." data-testid="filter-college"/>
      </div>
      <div>
        <label className={labelCls}>Budget (₹/mo)</label>
        <div className="grid grid-cols-2 gap-2">
          <input className={inputCls} type="number" placeholder="Min" value={minPrice} onChange={e=>setMinPrice(e.target.value)} data-testid="filter-min"/>
          <input className={inputCls} type="number" placeholder="Max" value={maxPrice} onChange={e=>setMaxPrice(e.target.value)} data-testid="filter-max"/>
        </div>
      </div>
      <div>
        <label className={labelCls}>Gender</label>
        <div className="flex flex-wrap gap-2">
          {GENDERS.map(g => (
            <button type="button" key={g} onClick={()=>setGender(gender===g ? "" : g)}
              className={`px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider transition ${
                gender===g
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"
              }`}
              data-testid={`filter-gender-${g}`}>
              {g}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl transition" type="submit" data-testid="apply-filters-btn">
          <Search size={14}/> Apply
        </button>
        <button type="button" className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition" onClick={clearAll} data-testid="clear-filters-btn">Clear</button>
      </div>
    </form>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-10" data-testid="properties-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="inline-block px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-semibold uppercase tracking-wider">All PGs</span>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-3 tracking-tight text-[#0E1F18]">
            {city ? `PGs in ${city}` : "Browse all PGs"}
          </h1>
          <p className="text-gray-500 mt-2">{loading ? "Loading..." : `${items.length} stay${items.length===1?"":"s"} found`}</p>
        </div>
        <button className="md:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 font-semibold bg-white hover:bg-gray-50 transition" onClick={()=>setFiltersOpen(true)} data-testid="open-filters-btn">
          <SlidersHorizontal size={16}/> Filters
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className="hidden md:block bg-white rounded-2xl border border-gray-200 p-5 h-fit sticky top-24" data-testid="filters-aside">
          <h3 className="font-display font-black text-xl mb-4 text-[#0E1F18]">Filters</h3>
          {FiltersBody}
        </aside>

        <div>
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_,i)=>(<div key={i} className="rounded-2xl h-80 animate-pulse bg-gray-100"/>))}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center" data-testid="empty-state">
              <div className="font-display font-black text-3xl text-[#0E1F18]">No PGs found.</div>
              <p className="text-gray-500 mt-2">Try clearing filters or searching another city.</p>
              <button className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 rounded-xl mt-5 transition" onClick={clearAll}>Reset filters</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
              {items.map(p => <PropertyCard key={p.id} p={p}/>)}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 md:hidden" onClick={()=>setFiltersOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white p-5 overflow-y-auto shadow-2xl" onClick={e=>e.stopPropagation()} data-testid="filters-drawer">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-2xl text-[#0E1F18]">Filters</h3>
              <button onClick={()=>setFiltersOpen(false)} className="p-2 rounded-full hover:bg-gray-100"><X size={18}/></button>
            </div>
            {FiltersBody}
          </div>
        </div>
      )}
    </div>
  );
}
